import type { FC } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import {
  EXPENSE_CATEGORIES,
  type ExpenseSubcategory,
  type ExpenseTax,
} from '@pflegenavi/frontend/api-nursing-home';
import { Typography } from '@pflegenavi/web-components';
import { useFormatting } from '@pflegenavi/frontend/localization';
import { ExpensePartRow } from './ExpensePartRow';
import { createInternalId, type PartDraft } from './expensePartsDrafts';

export type { PartDraft } from './expensePartsDrafts';
export {
  expensePartsToDrafts,
  draftsToUpdateParts,
  hasZeroAmountPart,
  hasArchivedSubcategoryPart,
  hasArchivedTaxPart,
} from './expensePartsDrafts';

export interface ExpensePartsEditorProps {
  parts: PartDraft[];
  onChange: (parts: PartDraft[]) => void;
  groupAmount: number;
  subcategories: ExpenseSubcategory[];
  taxCodes: ExpenseTax[];
  readOnly?: boolean;
  errorMessage?: string | null;
}

export const ExpensePartsEditor: FC<ExpensePartsEditorProps> = ({
  parts,
  onChange,
  groupAmount,
  subcategories,
  taxCodes,
  readOnly = false,
  errorMessage,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  // Row added via the button gets the cursor in its amount field.
  const [focusPartId, setFocusPartId] = useState<string | null>(null);

  const partsSum = useMemo(
    () => parts.reduce((acc, p) => acc + (p.amount || 0), 0),
    [parts]
  );
  const delta = partsSum - groupAmount;
  const sumMatches = delta === 0;

  const groupedSubcategories = useMemo(() => {
    const byCategory = new Map<string, ExpenseSubcategory[]>();
    for (const sub of subcategories) {
      if (sub.archived) {
        continue;
      }
      const list = byCategory.get(sub.category) ?? [];
      list.push(sub);
      byCategory.set(sub.category, list);
    }
    return EXPENSE_CATEGORIES.flatMap((cat) => {
      const items = byCategory.get(cat) ?? [];
      if (items.length === 0) {
        return [];
      }
      return [{ category: cat, items }];
    });
  }, [subcategories]);

  const updatePart = (internalId: string, patch: Partial<PartDraft>) => {
    onChange(
      parts.map((p) => (p._internalId === internalId ? { ...p, ...patch } : p))
    );
  };

  const removePart = (internalId: string) => {
    onChange(parts.filter((p) => p._internalId !== internalId));
  };

  const addPart = () => {
    const _internalId = createInternalId();
    setFocusPartId(_internalId);
    onChange([
      ...parts,
      {
        _internalId,
        amount: groupAmount - partsSum,
        expense_subcategory_id: null,
        expense_tax_id: null,
      },
    ]);
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">
        {t('accounting.expenses.parts.section-title')}
      </Typography>

      <Stack spacing={1}>
        {parts.map((part) => (
          <ExpensePartRow
            key={part._internalId}
            part={part}
            subcategories={subcategories}
            groupedSubcategories={groupedSubcategories}
            taxCodes={taxCodes}
            readOnly={readOnly}
            canRemove={parts.length > 1}
            autoFocus={part._internalId === focusPartId}
            onAmountChange={(amount) =>
              updatePart(part._internalId, { amount })
            }
            onSubcategoryChange={(expense_subcategory_id) =>
              updatePart(part._internalId, { expense_subcategory_id })
            }
            onTaxCodeChange={(expense_tax_id) =>
              updatePart(part._internalId, { expense_tax_id })
            }
            onCommentaryChange={(commentary) =>
              updatePart(part._internalId, { commentary })
            }
            onRemove={() => removePart(part._internalId)}
          />
        ))}
      </Stack>

      {!readOnly && (
        <Button
          onClick={addPart}
          startIcon={<AddIcon />}
          size="small"
          color="inherit"
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('accounting.expenses.parts.add-button')}
        </Button>
      )}

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="flex-end"
      >
        {sumMatches ? (
          <>
            <CheckIcon fontSize="small" color="success" />
            <Typography variant="body2">
              {t('accounting.expenses.parts.sum-match', {
                amount: fCurrencyCents(partsSum),
              })}
            </Typography>
          </>
        ) : (
          <Typography variant="body2" color="error.main">
            {t('accounting.expenses.parts.sum-mismatch', {
              sum: fCurrencyCents(partsSum),
              target: fCurrencyCents(groupAmount),
              delta: fCurrencyCents(delta),
            })}
          </Typography>
        )}
      </Stack>

      {errorMessage && (
        <Typography variant="caption" color="error.main">
          {errorMessage}
        </Typography>
      )}
    </Stack>
  );
};
